// 篩選狀態 ↔ 網址。網址是唯一的狀態來源（可分享、可加書籤、上一頁可還原）；
// 網址沒有帶參數時，才用瀏覽器記住的偏好。
import { GENRES, type GenreKey, type PlatformKey } from '@dayone/core/dictionaries';
import { DEFAULT_PREFS, PAGE_SIZES, type PageSize, type Prefs, sanitizePrefs } from '../storage/prefs.ts';

export interface FilterState extends Prefs {
  q: string;
  /** 類型（任一符合即可；GDO-18）。跟搜尋一樣只寫在網址，不記在瀏覽器 */
  genres: GenreKey[];
  /** 'YYYY-MM' */
  from: string | null;
  to: string | null;
  page: number;
  /** 詳情抽屜開啟中的遊戲 ID */
  game: string | null;
  /** 月曆檢視中的月份 'YYYY-MM'（有值時顯示月曆；不是偏好，不記在瀏覽器） */
  month: string | null;
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
/** 遊戲 ID 格式（g_<igdbId> 或暫時 ID p_<ulid>）；不合法的值直接忽略 */
const GAME_ID = /^[gp]_[A-Za-z0-9]{1,40}$/;
const GENRE_KEYS = new Set<string>(GENRES.map((g) => g.key));

/** 網址參數名稱（短，方便分享） */
const P = {
  status: 'st',
  platforms: 'pf',
  q: 'q',
  genres: 'gn',
  from: 'from',
  to: 'to',
  sort: 's',
  view: 'view',
  page: 'page',
  size: 'size',
  game: 'g',
  layout: 'lay',
  month: 'm',
};

/** 這些參數出現時代表網址帶有篩選意圖，偏好不覆蓋 */
const PREF_PARAMS = [P.status, P.platforms, P.sort, P.view, P.size, P.layout];

export function parseState(search: string, prefs: Prefs = DEFAULT_PREFS): FilterState {
  const sp = new URLSearchParams(search);
  const list = (k: string) => sp.get(k)?.split(',').filter(Boolean);
  const fromUrl = sanitizePrefs({
    status: list(P.status) ?? prefs.status,
    platforms: list(P.platforms) ?? prefs.platforms,
    sort: sp.get(P.sort) ?? prefs.sort,
    view: sp.get(P.view) ?? prefs.view,
    pageSize: sp.has(P.size) ? Number(sp.get(P.size)) : prefs.pageSize,
    layout: sp.get(P.layout) ?? prefs.layout,
  });
  const month = (k: string) => {
    const v = sp.get(k);
    return v && MONTH.test(v) ? v : null;
  };
  let from = month(P.from);
  let to = month(P.to);
  if (from && to && from > to) [from, to] = [to, from];
  const page = Number(sp.get(P.page));
  return {
    ...fromUrl,
    q: (sp.get(P.q) ?? '').slice(0, 100),
    genres: [...new Set(list(P.genres) ?? [])].filter((g): g is GenreKey => GENRE_KEYS.has(g)),
    from,
    to,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    game: GAME_ID.test(sp.get(P.game) ?? '') ? sp.get(P.game) : null,
    month: month(P.month),
  };
}

export function hasPrefParams(search: string): boolean {
  const sp = new URLSearchParams(search);
  return PREF_PARAMS.some((k) => sp.has(k));
}

/** 只寫出與預設不同的參數，網址保持簡短 */
export function serializeState(s: FilterState): string {
  const sp = new URLSearchParams();
  const eq = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
  if (!eq(s.status, DEFAULT_PREFS.status)) sp.set(P.status, s.status.join(','));
  if (s.platforms.length) sp.set(P.platforms, s.platforms.join(','));
  if (s.q) sp.set(P.q, s.q);
  if (s.genres.length) sp.set(P.genres, s.genres.join(','));
  if (s.from) sp.set(P.from, s.from);
  if (s.to) sp.set(P.to, s.to);
  if (s.sort !== DEFAULT_PREFS.sort) sp.set(P.sort, s.sort);
  if (s.view !== DEFAULT_PREFS.view) sp.set(P.view, s.view);
  if (s.layout !== DEFAULT_PREFS.layout) sp.set(P.layout, s.layout);
  if (s.month) sp.set(P.month, s.month);
  if (s.game) sp.set(P.game, s.game);
  if (s.view === 'pages') {
    if (s.pageSize !== DEFAULT_PREFS.pageSize) sp.set(P.size, String(s.pageSize));
    if (s.page > 1) sp.set(P.page, String(s.page));
  }
  const str = sp.toString().replaceAll('%2C', ',');
  return str ? `?${str}` : '';
}

export const prefsOf = (s: FilterState): Prefs => ({
  status: s.status,
  platforms: s.platforms,
  sort: s.sort,
  view: s.view,
  pageSize: s.pageSize,
  layout: s.layout,
});

/** 可以個別清除的條件（空結果時的引導用） */
export type ClearableFilter = 'q' | 'platforms' | 'genres' | 'date';

export function activeFilters(s: FilterState): ClearableFilter[] {
  const out: ClearableFilter[] = [];
  if (s.q) out.push('q');
  if (s.platforms.length) out.push('platforms');
  if (s.genres.length) out.push('genres');
  if (s.from || s.to) out.push('date');
  return out;
}

export function clearFilter(s: FilterState, f: ClearableFilter | 'all'): FilterState {
  const next = { ...s, page: 1 };
  if (f === 'q' || f === 'all') next.q = '';
  if (f === 'platforms' || f === 'all') next.platforms = [];
  if (f === 'genres' || f === 'all') next.genres = [];
  if (f === 'date' || f === 'all') {
    next.from = null;
    next.to = null;
  }
  return next;
}

export const togglePlatform = (list: PlatformKey[], p: PlatformKey) =>
  list.includes(p) ? list.filter((x) => x !== p) : [...list, p];

export const toggleGenre = (list: GenreKey[], g: GenreKey) =>
  list.includes(g) ? list.filter((x) => x !== g) : [...list, g];

export const isPageSize = (n: number): n is PageSize => (PAGE_SIZES as readonly number[]).includes(n);
